import { useLocation } from "react-router-dom"
import { useActivePromotionForItem } from "@/features/promotions/ActivePromotionProvider"
import { treatmentCatalogId, type TreatmentId } from "@/content/treatments"
import { getServiceIdBySlug } from "@/content/services"

type PromoPathKind = "productos" | "servicios" | "tratamientos"

interface PromoPathMatch {
	kind: PromoPathKind
	/** Id de catálogo ya resuelto (para servicios, el `ServiceId` interno, no el slug de URL). */
	id: string
}

/**
 * Banner de promoción bajo el NavBar.
 * Solo en detalle de producto/servicio/tratamiento cuando ese ítem está en una
 * promo activa. Los tratamientos se promocionan por paquete (Básico/Completo)
 * por separado, así que se revisan ambos y se muestra el primero con promo.
 */
export default function PromotionBanner(): React.JSX.Element | null {
	const { pathname } = useLocation()
	const match = promoPathMatchFromPath(pathname)

	const treatmentBasico = useActivePromotionForItem(
		match?.kind === "tratamientos" ? treatmentCatalogId(match.id as TreatmentId, "two") : undefined
	)
	const treatmentCompleto = useActivePromotionForItem(
		match?.kind === "tratamientos" ? treatmentCatalogId(match.id as TreatmentId, "four") : undefined
	)
	const productOrService = useActivePromotionForItem(match && match.kind !== "tratamientos" ? match.id : undefined)

	const promo = match?.kind === "tratamientos" ? (treatmentCompleto ?? treatmentBasico) : productOrService

	if (!promo?.message) return null

	return (
		<div className="w-full bg-primary text-primary-foreground">
			<p className="mx-auto max-w-6xl px-4 py-2 text-center text-sm font-medium sm:py-2.5 sm:text-base">{promo.message}</p>
		</div>
	)
}

/** Extrae el slug de `/productos/:slug`, `/servicios/:slug` o `/tratamientos/:slug`; null en listados u otras rutas. */
function promoPathMatchFromPath(pathname: string): PromoPathMatch | null {
	const path = pathname.replace(/\/$/, "") || "/"
	const match = /^\/(productos|servicios|tratamientos)\/([^/]+)$/.exec(path)
	if (!match) return null
	const kind = match[1] as PromoPathKind
	const slug = match[2]!
	if (kind === "servicios") {
		const serviceId = getServiceIdBySlug(slug)
		return serviceId ? { kind, id: serviceId } : null
	}
	return { kind, id: slug }
}
